import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect } from 'react';

import { Flex } from '~/components/blocks';
import { CancelPane } from '~/components/partials';
import { CancelUnavailableSendMessagePane } from '~/components/partials/CancelUnavailableSendMessagePane';
import { ActionButton } from '~/components/partials/ReceptionDrawer/ActionButton';
import { InactiveButton } from '~/components/partials/ReceptionDrawer/InactiveButton';
import { LineVisitorAppointmentDetailFragment } from '~/graphql/generated/graphql';
import { cancelPane } from '~/state/partials/cancel_pane/atoms';
import { cancelUnavailableSendMessagePane } from '~/state/partials/cancel_unavailable_send_message_pane/atoms';
import {
  lineVisitorChargeModalState,
  lineVisitorNotificationConfirmModalState,
  lineVisitorNotificationCongestionModalState,
  lineVisitorSendUnavailableModalState,
} from '~/state/reception/atoms';

import { useCancelAppointment } from './use-cancel-appointment';
import { useCancelAppointmentWithoutMessage } from './use-cancel-appointment-without-message';

type Props = {
  appointment: LineVisitorAppointmentDetailFragment;
};

export const BookedButtons = (props: Props) => {
  const theme = useTheme();
  const isSentCongestionNotification = !!props.appointment.congestionNotificationSentAt;
  const messageSendable = props.appointment.lineVisitor?.messageSendable ?? false;

  const { canceling, handleCancel } = useCancelAppointment(props.appointment.id);
  const { canceling: unavailableSendCanceling, handleCancel: handleUnavailableSendCancel } =
    useCancelAppointmentWithoutMessage(props.appointment.id);

  const setChargeModalState = useSetAtom(lineVisitorChargeModalState);
  const setNotificationConfirmState = useSetAtom(lineVisitorNotificationConfirmModalState);
  const setSendUnavailableModalState = useSetAtom(lineVisitorSendUnavailableModalState);
  const setNotificationCongestionModalState = useSetAtom(
    lineVisitorNotificationCongestionModalState,
  );
  const resetCancelUnavailableSendMessagePane = useResetAtom(cancelUnavailableSendMessagePane);
  const resetCancelPane = useResetAtom(cancelPane);
  const setCancelPane = useSetAtom(cancelPane);
  const setCancelUnavailableSendMessagePane = useSetAtom(cancelUnavailableSendMessagePane);

  // 準備できたよ通知
  // モーダルで準備完了通知送信を確定したあと、指導待ち -> 未会計 （telemedicine: false）
  const handleSendNotification = useCallback(() => {
    if (!messageSendable) {
      setSendUnavailableModalState({ isOpen: true });
      return;
    }
    setNotificationConfirmState({
      isOpen: true,
      appointmentId: props.appointment.id,
      lineVisitorId: props.appointment.lineVisitor?.id ?? null,
      webVisitorId: null,
    });
  }, [
    messageSendable,
    props.appointment.id,
    props.appointment.lineVisitor?.id,
    setSendUnavailableModalState,
    setNotificationConfirmState,
  ]);

  const handleCongestionClick = useCallback(() => {
    if (!messageSendable) {
      setSendUnavailableModalState({ isOpen: true });
      return;
    }

    setNotificationCongestionModalState({
      isOpen: true,
      appointmentId: props.appointment.id,
      webVisitorId: null,
      lineVisitorId: props.appointment.lineVisitor?.id as string,
    });
  }, [
    messageSendable,
    props.appointment.id,
    props.appointment.lineVisitor?.id,
    setNotificationCongestionModalState,
    setSendUnavailableModalState,
  ]);

  // 会計モーダルで確定後
  // - 未会計 -> 会計済(配送待ち) （telemedicine: true）
  // - 指導待ち -> 未会計 -> 完了 （telemedicine: false）
  const openChargeModal = useCallback(() => {
    setChargeModalState({
      isOpen: true,
      appointmentId: props.appointment.id,
    });
  }, [setChargeModalState, props.appointment.id]);

  // キャンセル
  const handleClickCancel = useCallback(() => {
    if (!messageSendable) {
      setCancelUnavailableSendMessagePane({ isOpen: true });
      return;
    }
    setCancelPane({ isOpen: true });
  }, [messageSendable, setCancelPane, setCancelUnavailableSendMessagePane]);

  useEffect(() => {
    resetCancelPane();
    resetCancelUnavailableSendMessagePane();
  }, [props.appointment.id, resetCancelUnavailableSendMessagePane, resetCancelPane]);

  return (
    <>
      <Flex flexDirection="column">
        <Flex alignItems="center">
          {isSentCongestionNotification ? (
            <InactiveButton status="送信済" wide="half" size="m">
              混雑時通知を送信
            </InactiveButton>
          ) : (
            <ActionButton use="primaryInverse" wide="half" size="m" onClick={handleCongestionClick}>
              混雑時通知を送信
            </ActionButton>
          )}
          <ActionButton
            use="primaryInverse"
            wide="half"
            size="m"
            marginLeft={theme.space.m}
            onClick={handleSendNotification}
          >
            準備完了通知を送信
          </ActionButton>
        </Flex>
        <ActionButton
          marginTop={theme.space.m}
          use="primary"
          wide="fill"
          size="l"
          onClick={openChargeModal}
        >
          会計
        </ActionButton>
        <ActionButton use="bgGrey" onClick={handleClickCancel} marginTop={theme.space.m} size="s">
          キャンセル
        </ActionButton>
      </Flex>
      <CancelPane align="center" canceling={canceling} onCancel={handleCancel} />
      <CancelUnavailableSendMessagePane
        align="center"
        canceling={unavailableSendCanceling}
        onCancel={handleUnavailableSendCancel}
      />
    </>
  );
};
